import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth-context";
import { useState, useEffect } from "react";
import { Plus, Trash2, Flag, Pencil } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/tasks")({
  head: () => ({ meta: [{ title: "Tasks — CEO Boardroom" }] }),
  component: TasksPage,
});

type Task = {
  id: string;
  title: string;
  notes: string | null;
  due_date: string | null;
  priority: string;
  done: boolean;
  completed_at: string | null;
};

const priorityMeta: Record<string, { label: string; color: string }> = {
  high: { label: "High", color: "text-[color:var(--danger)]" },
  normal: { label: "Normal", color: "text-muted-foreground" },
  low: { label: "Low", color: "text-[color:var(--teal)]" },
};

import { localTodayISO } from "@/lib/money";
function todayISO() { return localTodayISO(); }
function weekEndISO() {
  const d = new Date();
  d.setDate(d.getDate() + (7 - d.getDay()));
  return d.toISOString().slice(0, 10);
}

function TasksPage() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<Task | null>(null);
  const [filter, setFilter] = useState<"today" | "week" | "all" | "done">("all");

  const { data: tasks = [] } = useQuery({
    queryKey: ["tasks", user?.id],
    enabled: !!user,
    queryFn: async () => {
      const { data } = await supabase.from("tasks").select("*").eq("user_id", user!.id).order("done").order("due_date", { nullsFirst: false }).order("created_at", { ascending: false });
      return (data ?? []) as Task[];
    },
  });

  const today = todayISO();
  const weekEnd = weekEndISO();
  const filtered = tasks.filter((t) => {
    if (filter === "done") return t.done;
    if (t.done) return false;
    if (filter === "today") return t.due_date && t.due_date <= today;
    if (filter === "week") return !t.due_date || t.due_date <= weekEnd;
    return true;
  });

  const openCount = tasks.filter((t) => !t.done).length;
  const overdue = tasks.filter((t) => !t.done && t.due_date && t.due_date < today).length;

  const toggle = useMutation({
    mutationFn: async ({ id, done }: { id: string; done: boolean }) => {
      const { error } = await supabase.from("tasks").update({ done, completed_at: done ? new Date().toISOString() : null }).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["tasks"] }),
  });

  const del = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("tasks").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["tasks"] }),
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl font-semibold">Tasks</h1>
          <p className="text-sm text-muted-foreground mt-1">What has to get done. Nothing else.</p>
        </div>
        <Button onClick={() => { setEditing(null); setOpen(true); }} className="bg-[color:var(--gold)] text-[color:var(--primary-foreground)]">
          <Plus className="h-4 w-4 mr-1.5" /> New Task
        </Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <Mini label="Open" value={String(openCount)} />
        <Mini label="Overdue" value={String(overdue)} tone={overdue > 0 ? "danger" : undefined} />
        <Mini label="Done" value={String(tasks.filter((t) => t.done).length)} />
      </div>

      <div className="flex gap-2 flex-wrap">
        {(["today", "week", "all", "done"] as const).map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`px-3 py-1.5 rounded-md text-xs uppercase tracking-wider ${filter === f ? "bg-[color:var(--gold)] text-[color:var(--primary-foreground)]" : "bg-[color:var(--surface-2)] text-muted-foreground hover:text-foreground"}`}
          >
            {f}
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <div className="boardroom-card p-10 text-center">
          <p className="text-foreground/80 mb-2">Nothing here.</p>
          <p className="text-sm text-muted-foreground">Add a task to focus your day.</p>
        </div>
      ) : (
        <div className="boardroom-card divide-y divide-[color:var(--hairline)]">
          {filtered.map((t) => {
            const overdueTask = !t.done && t.due_date && t.due_date < today;
            return (
              <div key={t.id} className="p-4 flex items-start gap-3 group">
                <Checkbox
                  checked={t.done}
                  onCheckedChange={(v) => toggle.mutate({ id: t.id, done: !!v })}
                  className="mt-0.5"
                />
                <div className="flex-1 min-w-0">
                  <div className={`text-sm ${t.done ? "line-through text-muted-foreground" : "text-foreground"}`}>{t.title}</div>
                  {t.notes && <div className="text-xs text-muted-foreground mt-0.5">{t.notes}</div>}
                  <div className="flex items-center gap-3 mt-1.5 text-[10px] uppercase tracking-wider">
                    {t.due_date && (
                      <span className={`tabular ${overdueTask ? "text-[color:var(--danger)]" : "text-muted-foreground"}`}>
                        Due {new Date(t.due_date + "T00:00").toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                      </span>
                    )}
                    <span className={`flex items-center gap-1 ${priorityMeta[t.priority]?.color}`}>
                      <Flag className="h-2.5 w-2.5" /> {priorityMeta[t.priority]?.label ?? t.priority}
                    </span>
                  </div>
                </div>
                <button onClick={() => { setEditing(t); setOpen(true); }} className="opacity-100 md:opacity-0 md:group-hover:opacity-100 text-muted-foreground hover:text-foreground transition p-1" aria-label="Edit">
                  <Pencil className="h-4 w-4" />
                </button>
                <button onClick={() => { if (confirm("Delete this task?")) del.mutate(t.id); }} className="opacity-100 md:opacity-0 md:group-hover:opacity-100 text-muted-foreground hover:text-[color:var(--danger)] transition p-1" aria-label="Delete">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            );
          })}
        </div>
      )}

      <NewTaskDialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setEditing(null); }} userId={user?.id} editing={editing} />
    </div>
  );
}

function Mini({ label, value, tone }: { label: string; value: string; tone?: "danger" }) {
  return (
    <div className="boardroom-card p-4">
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className={`font-display text-xl font-semibold tabular mt-1 ${tone === "danger" ? "text-[color:var(--danger)]" : "text-[color:var(--gold)]"}`}>{value}</div>
    </div>
  );
}

function NewTaskDialog({ open, onOpenChange, userId, editing }: { open: boolean; onOpenChange: (v: boolean) => void; userId?: string; editing?: Task | null }) {
  const qc = useQueryClient();
  const [title, setTitle] = useState("");
  const [notes, setNotes] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [priority, setPriority] = useState("normal");

  useEffect(() => {
    if (open) {
      setTitle(editing?.title ?? "");
      setNotes(editing?.notes ?? "");
      setDueDate(editing?.due_date ?? "");
      setPriority(editing?.priority ?? "normal");
    }
  }, [open, editing?.id]);

  async function save() {
    if (!userId || !title.trim()) return;
    const payload = {
      title: title.trim(),
      notes: notes.trim() || null,
      due_date: dueDate || null,
      priority,
    };
    if (editing) {
      const { error } = await supabase.from("tasks").update(payload).eq("id", editing.id);
      if (error) return toast.error(error.message);
      toast.success("Task updated");
    } else {
      const { error } = await supabase.from("tasks").insert({ user_id: userId, ...payload });
      if (error) return toast.error(error.message);
      toast.success("Task added");
    }
    qc.invalidateQueries({ queryKey: ["tasks"] });
    onOpenChange(false);
    setTitle(""); setNotes(""); setDueDate(""); setPriority("normal");
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[color:var(--surface-1)] border-[color:var(--hairline)] max-w-md">
        <DialogHeader><DialogTitle>{editing ? "Edit Task" : "New Task"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label className="text-xs mb-1.5 block">Task</Label><Input value={title} onChange={(e) => setTitle(e.target.value)} autoFocus /></div>
          <div><Label className="text-xs mb-1.5 block">Notes (optional)</Label><Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs mb-1.5 block">Due date</Label><Input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} /></div>
            <div><Label className="text-xs mb-1.5 block">Priority</Label>
              <Select value={priority} onValueChange={setPriority}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="normal">Normal</SelectItem>
                  <SelectItem value="low">Low</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <Button onClick={save} className="w-full bg-[color:var(--gold)] text-[color:var(--primary-foreground)]">{editing ? "Save changes" : "Add Task"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

